import { api, fetchServer } from "@/features/utils/fetch-server";
import type {
  AvailableNumbers,
  AvailableNumberType,
  CompanyLookup,
  CreateNumberRequestData,
  NumberRequest,
  NumberRequestState,
  UpdateNumberRequestData,
} from "../types";

const base = (clientId: string) =>
  `/api/sessions/v1/${clientId}/number-requests`;

export class NumberRequestsAPI {
  static list = async (clientId: string): Promise<NumberRequest[]> => {
    const response = await api.get<{ requests: NumberRequest[] }>(
      base(clientId),
    );
    return response.requests || [];
  };

  static get = async (
    clientId: string,
    requestId: string,
  ): Promise<NumberRequest> => {
    const response = await api.get<{ request: NumberRequest }>(
      `${base(clientId)}/${requestId}`,
    );
    return response.request;
  };

  /** Who a SIREN is, from the French directory. Never fails on a bad SIREN. */
  static lookupCompany = async (
    clientId: string,
    siren: string,
  ): Promise<CompanyLookup> => {
    const response = await api.get<{ company: CompanyLookup }>(
      `${base(clientId)}/company?siren=${encodeURIComponent(siren)}`,
    );
    return response.company;
  };

  /**
   * Numbers the client can pick. With no `prefix`, the server suggests one
   * (the region of `postcode`/`address` for a local number, 06 for a mobile).
   */
  static availableNumbers = async (
    clientId: string,
    query: {
      type: AvailableNumberType;
      country?: string;
      prefix?: string;
      postcode?: string;
      address?: string;
    },
  ): Promise<AvailableNumbers> => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value) params.set(key, value);
    }
    return api.get<AvailableNumbers>(
      `${base(clientId)}/available-numbers?${params.toString()}`,
    );
  };

  static create = async (
    clientId: string,
    data: CreateNumberRequestData,
  ): Promise<NumberRequest> => {
    const response = await api.post<
      { request: NumberRequest },
      CreateNumberRequestData
    >(base(clientId), data);
    return response.request;
  };

  static update = async (
    clientId: string,
    requestId: string,
    data: UpdateNumberRequestData,
  ): Promise<NumberRequest> => {
    const response = await api.put<
      { request: NumberRequest },
      UpdateNumberRequestData
    >(`${base(clientId)}/${requestId}`, data);
    return response.request;
  };

  /**
   * Attach a supporting document to one checklist entry.
   * `fetchServer` rather than `api.post` — this is multipart, not JSON.
   */
  static uploadDocument = async (
    clientId: string,
    requestId: string,
    type: string,
    file: File,
  ): Promise<NumberRequest> => {
    const body = new FormData();
    body.append("file", file);

    const response = await fetchServer(
      `${base(clientId)}/${requestId}/documents/${type}`,
      { method: "POST", body },
    );

    return (await response.json()).request;
  };

  static removeDocument = async (
    clientId: string,
    requestId: string,
    type: string,
  ): Promise<NumberRequest> => {
    const response = await api.delete<{ request: NumberRequest }>(
      `${base(clientId)}/${requestId}/documents/${type}`,
    );
    return response.request;
  };

  /**
   * Fetch an attached document as a blob. The route is authenticated, so the
   * file cannot simply be linked to — it has to come through `fetchServer` and
   * be handed to the browser as an object URL.
   */
  static downloadDocument = async (
    clientId: string,
    requestId: string,
    type: string,
  ): Promise<Blob> => {
    const response = await fetchServer(
      `${base(clientId)}/${requestId}/documents/${type}/download`,
      { method: "GET" },
    );

    return response.blob();
  };

  /** Advance the approval state machine. Refused server-side if illegal. */
  static transition = async (
    clientId: string,
    requestId: string,
    state: NumberRequestState,
    comment?: string,
  ): Promise<NumberRequest> => {
    const response = await api.post<
      { request: NumberRequest },
      { state: NumberRequestState; comment?: string }
    >(`${base(clientId)}/${requestId}/transition`, { state, comment });
    return response.request;
  };

  static remove = async (
    clientId: string,
    requestId: string,
  ): Promise<{ success: boolean }> =>
    api.delete<{ success: boolean }>(`${base(clientId)}/${requestId}`);
}
